'use client';
import * as React from 'react';
import { Button, Icon } from '@/components/site/ui';
import { cn } from '@/lib/utils';

/** THE time-slot pill — public booking (soft/outline) AND the in-app grid
 *  (reschedule, transfers, procedure, agenda free-hours) render this one
 *  component. Selected = solid green everywhere (the audit found a split public /
 *  in-app look for the same action). */
export function SlotPill({
  selected = false,
  variant = 'soft',
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  selected?: boolean;
  /** 'soft' = filled green tint (booking dialog); 'outline' = white with border
   *  (public profile/listing, in-app grid). Selected = solid green in both. */
  variant?: 'soft' | 'outline';
}) {
  return (
    <Button
      variant="unstyled"
      type="button"
      className={cn(
        'flex items-center justify-center rounded-md text-[0.8125rem] font-semibold transition-colors',
        props.disabled
          ? 'cursor-not-allowed border border-border bg-muted/60 text-muted-foreground line-through'
          : selected
            ? 'bg-medlux text-medlux-foreground'
            : variant === 'outline'
              ? 'border border-input bg-white text-foreground hover:border-medlux hover:text-medlux'
              : 'bg-medlux-soft text-medlux-soft-foreground hover:bg-medlux hover:text-medlux-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
}

/** Day cell for slot-picker day strips (week grid, day picker, modal strip).
 *  Semantics are explicit — the audit found the same tint meaning 'soonest' on
 *  one surface and 'selected' on another. Selected = solid green (matching the pill);
 *  soonest = the soft green anchor. Render inside a container that applies
 *  uppercase — labels arrive localized in their natural casing. */
export function DayCell({
  weekday,
  date,
  state = 'idle',
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  weekday: string;
  date: string;
  state?: 'idle' | 'selected' | 'soonest';
}) {
  const interactive = !!props.onClick;
  const inner = (
    <>
      <span
        className={cn(
          'text-[0.6875rem] font-semibold uppercase tracking-[0.08em]',
          state === 'selected'
            ? 'text-medlux-foreground'
            : state === 'soonest'
              ? 'text-medlux-soft-foreground'
              : 'text-foreground',
        )}
      >
        {weekday}
      </span>
      <span
        className={cn(
          'text-[0.6875rem] uppercase tracking-[0.04em]',
          state === 'selected' ? 'text-medlux-foreground/80' : 'text-muted-foreground',
        )}
      >
        {date}
      </span>
    </>
  );
  const classes = cn(
    'flex flex-col items-center rounded-lg border px-1 py-1.5 text-center transition-colors',
    state === 'selected'
      ? 'border-medlux bg-medlux'
      : state === 'soonest'
        ? 'border-medlux/30 bg-medlux-soft'
        : 'border-border bg-muted/40',
    interactive && state !== 'selected' && 'hover:border-medlux/40',
    className,
  );
  if (!interactive) return <div className={classes}>{inner}</div>;
  return (
    <Button
      variant="unstyled"
      type="button"
      aria-pressed={state === 'selected'}
      className={classes}
      {...props}
    >
      {inner}
    </Button>
  );
}

/** The show-more/show-less toggle under a collapsed slot grid (was pasted in
 *  two files with independent aria/rotation logic). */
export function ShowMoreToggle({
  expanded,
  onToggle,
  showMoreLabel,
  showLessLabel,
  className,
}: {
  expanded: boolean;
  onToggle: () => void;
  showMoreLabel: string;
  showLessLabel: string;
  className?: string;
}) {
  return (
    <Button
      variant="unstyled"
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      className={cn(
        'flex items-center justify-center gap-1.5 text-[0.8125rem] font-medium text-medlux-soft-foreground hover:underline',
        className,
      )}
    >
      {expanded ? showLessLabel : showMoreLabel}
      <Icon
        name="arrow-down-01"
        size={15}
        className={cn('transition-transform', expanded && 'rotate-180')}
      />
    </Button>
  );
}
